(ns hai.notes.mutations
  "Builders for the `:resolve` maps behind every note mutation. Each returns
   `{:query ... :transform-fn ...}` with the fresh note unwrapped from the
   response; surfaces assoc their own `:request-id`, `:store-in` and follow-up
   fx on top. Mutations that feed a mounted view join the full editor shape
   (`hai.notes.eql/note-eql`); callers that discard the result pass a narrower
   join."
  (:require
   [clojure.string :as str]
   [hai.notes.eql :as eql]))

(defn empty-payload?
  "Whether a save payload carries nothing — no text, links, tags, contacts,
   linked notes or tasks."
  [{:keys [text link-urls tag-uuids tag-names contact-uuids link-note-uuids
           task-uuids]}]
  (and (str/blank? text)
       (empty? link-urls) (empty? tag-uuids) (empty? tag-names)
       (empty? contact-uuids) (empty? link-note-uuids) (empty? task-uuids)))

(defn upsert-params
  "`note/upsert` params for a save `payload` — `:text`, `:link-urls`,
   `:tag-uuids`, `:tag-names`, `:untag-uuids`, `:contact-uuids`,
   `:link-note-uuids`, `:task-uuids` — against the note identified by
   `note-uuid` (nil creates one). A payload without `:text` leaves the note's
   text alone; creation always writes it, since note/upsert requires text."
  [{:keys [note-uuid source]}
   {:keys [text link-urls tag-uuids tag-names untag-uuids contact-uuids
           link-note-uuids task-uuids]
    :as payload}]
  (cond-> {:note/source source}
    (or (contains? payload :text) (nil? note-uuid))
    (assoc :note/text (or text ""))
    note-uuid (assoc :note/uuid note-uuid)
    (seq link-urls) (assoc :activity/urls link-urls)
    (seq tag-uuids) (assoc :note/tag-uuids tag-uuids)
    (seq tag-names) (assoc :note/tag-names tag-names)
    (seq untag-uuids) (assoc :note/untag-uuids untag-uuids)
    (seq contact-uuids) (assoc :note/contact-uuids contact-uuids)
    (seq link-note-uuids) (assoc :note/note-uuids link-note-uuids)
    (seq task-uuids) (assoc :note/task-uuids task-uuids)))

(defn- mutation-resolve
  [op params note-join]
  {:query [{(list op params) note-join}]
   :transform-fn (fn [resp] (get resp op))})

(defn upsert-resolve
  "Save through `note/upsert`. One arg joins the full editor shape; pass
   `note-join` (e.g. `[:note/uuid]`) when the caller discards the result."
  ([params]
   (upsert-resolve params eql/note-eql))
  ([params note-join]
   (mutation-resolve 'note/upsert params note-join)))

(defn remove-link-resolve
  [note-uuid link-url]
  (mutation-resolve 'note/remove-link
                    {:note/uuid note-uuid :activity/url link-url}
                    eql/note-eql))

(defn remove-contact-resolve
  [note-uuid contact-uuid]
  (mutation-resolve 'note/remove-contact
                    {:note/uuid note-uuid :contact/uuid contact-uuid}
                    eql/note-eql))

(defn remove-note-resolve
  [note-uuid linked-uuid]
  (mutation-resolve 'note/remove-note
                    {:note/uuid note-uuid :note/linked-uuid linked-uuid}
                    eql/note-eql))

(defn remove-task-resolve
  [note-uuid task-uuid]
  (mutation-resolve 'note/remove-task
                    {:note/uuid note-uuid :task/uuid task-uuid}
                    eql/note-eql))

(defn delete-resolve
  [note-uuid]
  {:query [(list 'note/delete {:note/uuid note-uuid})]})
